import { useEffect, useMemo, useState, type Dispatch, type SetStateAction } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Add, Delete, Edit } from "@mui/icons-material";
import { ConfirmModal } from "@/components/ConfirmModal";
import { DataTable } from "@/components/DataTable";
import { FilterModal } from "@/components/FilterModal";
import { CrudModal } from "@/components/crud/CrudModal";
import usePagination from "@/hooks/pagination";
import { useCrud } from "@/hooks/useCrud";
import { useAuth } from "@/context/AuthContext";
import { useNotification } from "@/context/NotificationContext";
import {
  deleteDistribuicaoArea,
  listDistribuicoesArea,
  saveDistribuicaoArea,
  type DePara,
  type DistribuicaoArea as DistribuicaoAreaApi,
  type MetodoDistribuicaoDiaria,
} from "@/api/metas.service";
import { normalizar } from "./metasFormato";

// Distribuição de Área: como a meta do mês é espalhada pelos dias, a partir do
// dia equivalente de um período de referência (bloco `distribuicao_diaria` do
// payload do cenário). Persistida pelo Product Runtime (metas.
// list_distribuicoes_area / save_distribuicao_area / delete_distribuicao_area).

const METODOS: { value: MetodoDistribuicaoDiaria; label: string }[] = [
  { value: "DIA_EQUIVALENTE", label: "Dia equivalente" },
];

interface Distribuicao {
  id?: string;
  nome: string;
  ativo: boolean;
  metodo: MetodoDistribuicaoDiaria;
  dataReferencia: string;
  /** Texto, para o campo aceitar digitação parcial; vira inteiro só ao gravar. */
  diasRetroativos: string;
  comparacaoInicio: string;
  comparacaoFim: string;
  dePara: DePara[];
}

type Rascunho = Omit<Distribuicao, "id">;

const DE_PARA_VAZIO: DePara = { data_inicio: "", data_fim: "", comparacao_inicio: "" };

const RASCUNHO_VAZIO: Rascunho = {
  nome: "",
  ativo: true,
  metodo: "DIA_EQUIVALENTE",
  dataReferencia: "",
  diasRetroativos: "",
  comparacaoInicio: "",
  comparacaoFim: "",
  dePara: [],
};

function daApi(d: DistribuicaoAreaApi): Distribuicao {
  const dd = d.distribuicao_diaria;
  return {
    id: d.id,
    nome: d.nome,
    ativo: d.ativo,
    metodo: dd.metodo,
    dataReferencia: dd.data_referencia,
    diasRetroativos: String(dd.dias_retroativos),
    comparacaoInicio: dd.comparacao_inicio ?? "",
    comparacaoFim: dd.comparacao_fim ?? "",
    dePara: dd.de_para ?? [],
  };
}

function paraApi(r: Rascunho, id?: string): DistribuicaoAreaApi {
  return {
    ...(id ? { id } : {}),
    nome: r.nome.trim(),
    ativo: r.ativo,
    distribuicao_diaria: {
      metodo: r.metodo,
      data_referencia: r.dataReferencia,
      dias_retroativos: Number(r.diasRetroativos),
      comparacao_inicio: r.comparacaoInicio || null,
      comparacao_fim: r.comparacaoFim || null,
      de_para: r.dePara.map(({ data_inicio, data_fim, comparacao_inicio }) => ({
        data_inicio,
        data_fim,
        comparacao_inicio,
      })),
    },
  };
}

function formatarData(iso: string) {
  return iso ? iso.split("-").reverse().join("/") : "—";
}

function rotuloMetodo(metodo: MetodoDistribuicaoDiaria) {
  return METODOS.find((m) => m.value === metodo)?.label ?? metodo;
}

const DIA_MS = 24 * 60 * 60 * 1000;
/** Limite do motor para uma linha do De/Para (fim − início ≤ 365 dias). */
const MAX_DIAS_DE_PARA = 365;

function diasEntre(inicio: string, fim: string) {
  return Math.round((Date.parse(fim) - Date.parse(inicio)) / DIA_MS);
}

function somarDias(iso: string, dias: number) {
  return new Date(Date.parse(iso) + dias * DIA_MS).toISOString().slice(0, 10);
}

function hojeIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// Mesmas regras do Product Runtime (validateDistribuicaoArea), que espelham as
// recusas do motor: o que depende do mês do cenário só é conferido ao calcular.
function validar(r: Rascunho) {
  const problemas: string[] = [];
  if (!r.nome.trim()) problemas.push("Informe o nome.");
  if (!r.dataReferencia) problemas.push("Informe a data de referência.");
  if (!/^[1-9]\d*$/.test(r.diasRetroativos.trim())) problemas.push("Os dias retroativos devem ser um inteiro maior que zero.");

  const comparacaoInformada = !!r.comparacaoInicio && !!r.comparacaoFim;
  if (!!r.comparacaoInicio !== !!r.comparacaoFim) {
    problemas.push("Informe o início e o fim da comparação, ou deixe os dois vazios.");
  } else if (comparacaoInformada && r.comparacaoFim < r.comparacaoInicio) {
    problemas.push("O fim da comparação não pode ser anterior ao início.");
  } else if (comparacaoInformada && r.comparacaoFim >= hojeIso()) {
    problemas.push("O período de comparação precisa já ter terminado: o fim deve ser anterior a hoje.");
  }

  r.dePara.forEach((linha, i) => {
    const n = i + 1;
    if (!linha.data_inicio || !linha.data_fim || !linha.comparacao_inicio) {
      problemas.push(`De/Para ${n}: preencha o início, o fim e o início da comparação.`);
    } else if (linha.data_fim < linha.data_inicio) {
      problemas.push(`De/Para ${n}: o fim não pode ser anterior ao início.`);
    } else if (diasEntre(linha.data_inicio, linha.data_fim) > MAX_DIAS_DE_PARA) {
      problemas.push(`De/Para ${n}: cada período pode ter no máximo ${MAX_DIAS_DE_PARA + 1} dias.`);
    }
  });

  // A comparação de uma linha cobre tantos dias quanto o período dela, a partir do início da comparação.
  const completas = r.dePara
    .map((l, i) => ({ ...l, n: i + 1 }))
    .filter(
      (l) =>
        l.data_inicio &&
        l.data_fim &&
        l.comparacao_inicio &&
        l.data_fim >= l.data_inicio &&
        diasEntre(l.data_inicio, l.data_fim) <= MAX_DIAS_DE_PARA
    )
    .map((l) => ({ ...l, comparacao_fim: somarDias(l.comparacao_inicio, diasEntre(l.data_inicio, l.data_fim)) }));

  const sobrepoe = (ai: string, af: string, bi: string, bf: string) => ai <= bf && bi <= af;
  let destinoSobreposto = false;
  let origemSobreposta = false;
  completas.forEach((a, i) =>
    completas.slice(i + 1).forEach((b) => {
      if (sobrepoe(a.data_inicio, a.data_fim, b.data_inicio, b.data_fim)) destinoSobreposto = true;
      if (sobrepoe(a.comparacao_inicio, a.comparacao_fim, b.comparacao_inicio, b.comparacao_fim)) origemSobreposta = true;
    })
  );
  if (destinoSobreposto) problemas.push("Os períodos do De/Para não podem se sobrepor.");
  if (origemSobreposta) {
    problemas.push("As comparações do De/Para não podem se sobrepor: cada dia de comparação só pode ser usado uma vez.");
  }

  if (comparacaoInformada && r.comparacaoFim >= r.comparacaoInicio) {
    completas
      .filter((l) => l.comparacao_inicio < r.comparacaoInicio || l.comparacao_fim > r.comparacaoFim)
      .forEach((l) =>
        problemas.push(
          `De/Para ${l.n}: a comparação (${formatarData(l.comparacao_inicio)} a ${formatarData(l.comparacao_fim)}) precisa estar dentro do período de comparação.`
        )
      );
  }
  return problemas;
}

interface DeParaTabelaProps {
  linhas: DePara[];
  onChange: (linhas: DePara[]) => void;
}

function DeParaTabela({ linhas, onChange }: DeParaTabelaProps) {
  const alterar = (indice: number, campo: keyof DePara, valor: string) =>
    onChange(linhas.map((l, i) => (i === indice ? { ...l, [campo]: valor } : l)));

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          De/Para de períodos
        </Typography>
        <Button size="small" startIcon={<Add />} onClick={() => onChange([...linhas, { ...DE_PARA_VAZIO }])}>
          Adicionar período
        </Button>
      </Stack>
      {linhas.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Nenhum período. Sem De/Para, todos os dias seguem o método escolhido.
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>INÍCIO</TableCell>
              <TableCell>FIM</TableCell>
              <TableCell>INÍCIO DA COMPARAÇÃO</TableCell>
              <TableCell padding="checkbox" />
            </TableRow>
          </TableHead>
          <TableBody>
            {linhas.map((linha, i) => (
              <TableRow key={i}>
                {(["data_inicio", "data_fim", "comparacao_inicio"] as const).map((campo) => (
                  <TableCell key={campo}>
                    <TextField
                      type="date"
                      size="small"
                      fullWidth
                      value={linha[campo]}
                      onChange={(e) => alterar(i, campo, e.target.value)}
                      slotProps={{ htmlInput: { "aria-label": `${campo} ${i + 1}` } }}
                    />
                  </TableCell>
                ))}
                <TableCell padding="checkbox">
                  <IconButton
                    color="error"
                    aria-label={`Remover período ${i + 1}`}
                    onClick={() => onChange(linhas.filter((_, j) => j !== i))}
                  >
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}

interface DistribuicaoFormProps {
  values: Partial<Distribuicao>;
  setValues: Dispatch<SetStateAction<Partial<Distribuicao>>>;
  isEditing: boolean;
  erros: string[];
  onFecharErros: () => void;
}

function DistribuicaoForm({ values, setValues, erros, onFecharErros }: DistribuicaoFormProps) {
  const form: Rascunho = { ...RASCUNHO_VAZIO, ...values };
  const patch = (parcial: Partial<Rascunho>) => setValues((prev) => ({ ...prev, ...parcial }));

  return (
    <Stack spacing={2}>
      <TextField
        label="Nome *"
        size="small"
        fullWidth
        value={form.nome}
        onChange={(e) => patch({ nome: e.target.value })}
      />
      <FormControlLabel
        control={<Checkbox checked={form.ativo} onChange={(e) => patch({ ativo: e.target.checked })} />}
        label="Ativo"
      />
      <TextField
        select
        label="Método *"
        size="small"
        fullWidth
        value={form.metodo}
        onChange={(e) => patch({ metodo: e.target.value as MetodoDistribuicaoDiaria })}
      >
        {METODOS.map((m) => (
          <MenuItem key={m.value} value={m.value}>
            {m.label}
          </MenuItem>
        ))}
      </TextField>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        <TextField
          type="date"
          label="Data de referência *"
          size="small"
          fullWidth
          value={form.dataReferencia}
          onChange={(e) => patch({ dataReferencia: e.target.value })}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          label="Dias retroativos *"
          size="small"
          fullWidth
          value={form.diasRetroativos}
          onChange={(e) => patch({ diasRetroativos: e.target.value })}
          slotProps={{ htmlInput: { inputMode: "numeric" } }}
        />
      </Stack>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        <TextField
          type="date"
          label="Início da comparação"
          size="small"
          fullWidth
          value={form.comparacaoInicio}
          onChange={(e) => patch({ comparacaoInicio: e.target.value })}
          helperText="Vazio: mesmo mês do ano anterior ao da meta"
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          type="date"
          label="Fim da comparação"
          size="small"
          fullWidth
          value={form.comparacaoFim}
          onChange={(e) => patch({ comparacaoFim: e.target.value })}
          slotProps={{ inputLabel: { shrink: true } }}
        />
      </Stack>

      <DeParaTabela linhas={form.dePara} onChange={(dePara) => patch({ dePara })} />

      {erros.length > 0 && (
        <Alert severity="error" onClose={onFecharErros}>
          {erros.join(" ")}
        </Alert>
      )}

      <Typography variant="caption" color="text.secondary">
        * Campos obrigatórios
      </Typography>
    </Stack>
  );
}

type DistribuicaoFiltro = {
  nome?: string;
  situacao?: string;
};

const FILTROS_VAZIOS = { nome: "", situacao: "" };

export function MetasDistribuicaoAreaTab() {
  const { user } = useAuth();
  const clientId = user!.loggedClient!.id;
  const { notify } = useNotification();
  const { page, setPage, rowsPerPage, setRowsPerPage } = usePagination();
  const { open, editing, handleCreate, handleEdit, handleClose } = useCrud<Distribuicao>();

  const [distribuicoes, setDistribuicoes] = useState<Distribuicao[]>([]);
  const [erros, setErros] = useState<string[]>([]);
  const [excluindo, setExcluindo] = useState<Distribuicao | null>(null);
  const [loading, setLoading] = useState(true);

  const [inputValues, setInputValues] = useState(FILTROS_VAZIOS);
  const [filters, setFilters] = useState<DistribuicaoFiltro>({});

  useEffect(() => {
    // Sem limpar, os registros do cliente anterior seguem na tela até a resposta nova chegar.
    setLoading(true);
    setDistribuicoes([]);
    listDistribuicoesArea(clientId)
      .then((salvas) => setDistribuicoes(salvas.map(daApi)))
      .catch(() => notify("Não foi possível carregar as distribuições.", "error"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const filtrados = useMemo(() => {
    const termo = normalizar(filters.nome ?? "");
    return distribuicoes.filter((d) => {
      if (termo && !normalizar(d.nome).includes(termo)) return false;
      if (filters.situacao && d.ativo !== (filters.situacao === "ativo")) return false;
      return true;
    });
  }, [distribuicoes, filters]);

  const paginados = useMemo(
    () => filtrados.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filtrados, page, rowsPerPage],
  );

  // Excluir os últimos registros pode deixar a página corrente vazia.
  useEffect(() => {
    if (page > 0 && paginados.length === 0) setPage(0);
  }, [page, paginados.length, setPage]);

  const abrirNovo = () => {
    setErros([]);
    handleCreate();
  };

  const abrirEdicao = (distribuicao: Distribuicao) => {
    setErros([]);
    handleEdit(distribuicao);
  };

  const onSave = async (values: Partial<Distribuicao>) => {
    const rascunho: Rascunho = { ...RASCUNHO_VAZIO, ...values };
    const problemas = validar(rascunho);
    setErros(problemas);
    if (problemas.length) return;

    let salva: Distribuicao;
    try {
      // A resposta traz o id gerado no insert, então é ela que entra na lista.
      salva = daApi(await saveDistribuicaoArea(paraApi(rascunho, editing?.id), clientId));
    } catch (err: any) {
      notify(err?.message || "Não foi possível salvar a distribuição.", "error");
      return;
    }

    setDistribuicoes((prev) => (editing ? prev.map((d) => (d.id === editing.id ? salva : d)) : [...prev, salva]));
    notify("Distribuição gravada.", "success");
    handleClose();
  };

  const onDeleteConfirm = async () => {
    if (!excluindo?.id) return;
    // Fecha a confirmação antes de chamar o back: se falhar, a lista fica
    // como está e o erro chega pela notificação.
    const alvo = excluindo;
    setExcluindo(null);
    try {
      await deleteDistribuicaoArea(alvo.id!, clientId);
    } catch (err: any) {
      notify(err?.message || "Não foi possível excluir a distribuição.", "error");
      return;
    }
    setDistribuicoes((prev) => prev.filter((d) => d.id !== alvo.id));
    notify("Distribuição excluída.", "success");
  };

  const applyFilters = () => {
    setFilters({
      nome: inputValues.nome || undefined,
      situacao: inputValues.situacao || undefined,
    });
    setPage(0);
  };

  const handleClearFilters = () => {
    setInputValues(FILTROS_VAZIOS);
    setFilters({});
    setPage(0);
  };

  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        gap={2}
        sx={{ mb: 2 }}
      >
        <Typography variant="body2" color="text.secondary">
          Como a meta do mês é distribuída pelos dias, a partir de um período de referência. Cada distribuição
          gravada fica salva na configuração de cálculo.
        </Typography>
        <Button variant="contained" startIcon={<Add />} onClick={abrirNovo} sx={{ flexShrink: 0 }}>
          Novo cadastro
        </Button>
      </Stack>

      <FilterModal activeCount={activeFilterCount} onApply={applyFilters} onClear={handleClearFilters}>
        <TextField
          label="Nome"
          size="small"
          fullWidth
          value={inputValues.nome}
          onChange={(e) => setInputValues((prev) => ({ ...prev, nome: e.target.value }))}
        />
        <TextField
          select
          label="Situação"
          size="small"
          fullWidth
          value={inputValues.situacao}
          onChange={(e) => setInputValues((prev) => ({ ...prev, situacao: e.target.value }))}
        >
          <MenuItem value="">Todas</MenuItem>
          <MenuItem value="ativo">Ativo</MenuItem>
          <MenuItem value="inativo">Inativo</MenuItem>
        </TextField>
      </FilterModal>

      <DataTable
        data={paginados}
        columns={[
          { key: "ativo", label: "Ativo", render: (d) => <Checkbox checked={d.ativo} disabled /> },
          { key: "nome", label: "Nome" },
          { key: "metodo", label: "Método", render: (d) => rotuloMetodo(d.metodo) },
          { key: "dataReferencia", label: "Referência", render: (d) => formatarData(d.dataReferencia) },
          { key: "diasRetroativos", label: "Dias retroativos" },
          { key: "dePara", label: "De/Para", render: (d) => d.dePara.length },
        ]}
        total={filtrados.length}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
        actions={(d) => (
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <IconButton onClick={() => abrirEdicao(d)} color="primary" aria-label={`Editar ${d.nome}`}>
              <Edit />
            </IconButton>
            <IconButton onClick={() => setExcluindo(d)} color="error" aria-label={`Excluir ${d.nome}`}>
              <Delete />
            </IconButton>
          </Stack>
        )}
      />

      {filtrados.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
          {distribuicoes.length === 0
            ? "Nenhuma distribuição gravada."
            : "Nenhuma distribuição encontrada para os filtros aplicados."}
        </Typography>
      )}

      {/* Montado só enquanto aberto para que cada abertura recomece do registro atual. */}
      {open && (
        <CrudModal<Distribuicao, Omit<DistribuicaoFormProps, "values" | "setValues" | "isEditing">>
          open
          onClose={handleClose}
          title={editing ? "Editar distribuição" : "Nova distribuição"}
          initialData={editing}
          onSubmit={onSave}
          Form={DistribuicaoForm}
          extraFormProps={{ erros, onFecharErros: () => setErros([]) }}
        />
      )}

      <ConfirmModal
        open={!!excluindo}
        onClose={() => setExcluindo(null)}
        onConfirm={onDeleteConfirm}
        title="Exclusão de distribuição"
        text={`Tem certeza que deseja excluir ${excluindo?.nome ?? "o registro"}?`}
        btnText="Confirmar"
      />
    </Box>
  );
}
